import { supabase } from '@/lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { fetchOrganizationData } from '@/utils/auth/organizationData';
import { Resend } from 'resend';
import { callFunction } from '@/utils/supabaseFunctions';

// Don't initialize Resend immediately to avoid errors if API key is missing
let resendInstance: Resend | null = null;

// Function to get or create the Resend instance
const getResend = (): Resend | null => {
  if (resendInstance) return resendInstance;
  
  // Check for API key in browser environment only
  const apiKey = import.meta.env?.VITE_RESEND_API_KEY;
    
  console.log('Attempting to initialize Resend for staff invitation with API key available:', !!apiKey);
  
  if (!apiKey) {
    console.warn('Resend API key not found in browser environment. Will use Edge Functions instead.');
    return null;
  }
  
  try {
    resendInstance = new Resend(apiKey);
    console.log('Resend initialized successfully for staff invitations');
    return resendInstance;
  } catch (error) {
    console.error('Failed to initialize Resend for staff invitations:', error);
    return null;
  }
};

interface StaffInvitationMetadata {
  staff_id: string;
  staffName: string;
  role: string;
  organization_id: string;
  organizationName?: string;
  firstName: string;
  lastName: string;
  created_by?: string;
}

export const StaffInvitationService = {
  /**
   * Send an invitation email to a new staff member
   */
  async sendStaffInvite(email: string, metadata: StaffInvitationMetadata): Promise<{ success: boolean; error: string | null; inviteLink?: string }> {
    try {
      console.log('Sending staff invitation email to:', email, 'with metadata:', metadata);
      
      // Generate a secure, unique token
      const token = uuidv4();
      const expiresAt = new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(); // 7 days from now

      // Use Supabase client directly instead of manual fetch
      console.log('Creating staff invitation record...');
      const { data, error } = await supabase
        .from('staff_invitations')
        .insert({
          email,
          organization_id: metadata.organization_id,
          staff_id: metadata.staff_id,
          token,
          expires_at: expiresAt,
          created_by: metadata.created_by || null
        })
        .select()
        .single();

      if (error) {
        console.error('Error inserting staff invitation:', error);
        throw new Error('Failed to create invitation record: ' + error.message);
      }
      
      console.log('Staff invitation record created successfully:', data);

      // Build the invite link with the token
      const siteUrl = import.meta.env.VITE_SITE_URL || "https://www.hivetivity.com";
      const inviteLink = `${siteUrl}/set-password?token=${encodeURIComponent(token)}&type=staff`;
      console.log('Generated staff invitation link:', inviteLink);

      // Build the email content
      const subject = "You've been invited to join the team!";
      const html = `
<p>Hello ${metadata.firstName || ''},</p>
<p>You've been invited to join <b>${metadata.organizationName || 'our team'}</b> as a ${metadata.role || 'staff member'}.</p>
<p>
  <a href="${inviteLink}" style="background:#2563eb;color:#fff;padding:10px 20px;border-radius:5px;text-decoration:none;">Accept Invitation</a>
</p>
<p>If you did not expect this invitation, you can ignore this email.</p>
`;

      // Try to send the email using the dedicated staff invitation edge function
      try {
        const sendResult = await callFunction('send-staff-invite', {
          method: 'POST',
          body: {
            email,
            staffName: `${metadata.firstName} ${metadata.lastName}`,
            organizationName: metadata.organizationName,
            role: metadata.role,
            inviteLink
          }
        });
        
        console.log('Staff email sent via dedicated edge function:', sendResult);
        
        return {
          success: true,
          error: null,
          inviteLink
        };
      } catch (sendError: any) {
        console.error('Error sending staff invitation via edge function:', sendError);
        // Continue with fallback methods instead of returning immediately
      }

      // Get the Resend instance for client-side sending as fallback
      const resend = getResend();
      
      // Check if Resend is properly configured
      if (!resend) {
        console.warn('Resend is not configured. Staff invitation email will not be sent, but invitation record was created successfully.');
        return { 
          success: true, 
          error: 'Email sending is disabled but invitation was created. Share this link manually.',
          inviteLink
        };
      }

      // Send the email with Resend
      console.log('Sending staff invitation email via Resend API...');
      const { error: resendError } = await resend.emails.send({
        from: 'Hivetivity <noreply@hivetivity.com>',
        to: email,
        subject,
        html,
      });

      if (resendError) {
        console.error('Resend staff invitation email error:', resendError);
        return {
          success: false,
          error: `Failed to send email: ${resendError.message}. Use this invitation link instead.`,
          inviteLink
        };
      }

      console.log('Staff invitation email sent successfully via Resend');
      return { 
        success: true, 
        error: null,
        inviteLink
      };

    } catch (error: any) {
      console.error('Error sending staff invitation:', error);
      return {
        success: false,
        error: error.message || 'Failed to send staff invitation'
      };
    }
  },

  /**
   * Get all staff invitations for an organization
   */
  async getStaffInvitations(organizationId: string) {
    try {
      const { data, error } = await supabase
        .from('staff_invitations')
        .select(`
          *,
          staff_members (
            id,
            first_name,
            last_name,
            email,
            role
          )
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching staff invitations:', error);
      return [];
    }
  },

  /**
   * Resend a staff invitation
   */
  async resendStaffInvite(invitationId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      // Get the invitation details
      const { data: invitation, error } = await supabase
        .from('staff_invitations')
        .select(`
          *,
          staff_members (
            id,
            first_name,
            last_name,
            email,
            role
          )
        `)
        .eq('id', invitationId)
        .single();

      if (error || !invitation) {
        throw new Error('Invitation not found');
      }

      // Get organization name
      let organizationName = undefined;
      try {
        const orgData = await fetchOrganizationData(invitation.organization_id);
        organizationName = orgData?.name;
      } catch (orgErr) {
        console.warn('Could not fetch organization name for invite:', orgErr);
      }

      // Generate new token and expiry
      const token = uuidv4();
      const expiresAt = new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString();

      // Update the invitation record
      const { error: updateError } = await supabase
        .from('staff_invitations')
        .update({
          token,
          expires_at: expiresAt,
          accepted_at: null // Reset if previously accepted
        })
        .eq('id', invitationId);

      if (updateError) throw updateError;

      // Resend the invitation
      return await this.sendStaffInvite(invitation.email, {
        staff_id: invitation.staff_id,
        staffName: `${invitation.staff_members.first_name} ${invitation.staff_members.last_name}`,
        role: invitation.staff_members.role,
        organization_id: invitation.organization_id,
        organizationName,
        firstName: invitation.staff_members.first_name,
        lastName: invitation.staff_members.last_name
      });

    } catch (error: any) {
      console.error('Error resending staff invitation:', error);
      return {
        success: false,
        error: error.message || 'Failed to resend staff invitation'
      };
    }
  }
}; 